import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export enum DocumentStatus {
  pending = 'Pending',
  uploading = 'Uploading',
  processing = 'Processing',
  success = 'Success',
  failed = 'Failed',
}

type StatusTagProps = {
  status: DocumentStatus
}

const statusStyles: Record<DocumentStatus, string> = {
  [DocumentStatus.pending]: 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800/50',
  [DocumentStatus.uploading]: 'text-purple-700 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30',
  // UI-26: orange, not yellow. The stats banner and the "Status" filter both
  // read processing off STATUS_META (orange); this pill was the third surface
  // and the only one still yellow, so the same document looked one colour in
  // the table and another in the banner above it. Hues match STATUS_META; the
  // shades differ because a pill needs a light plate and dark text where an
  // icon needs a single tint.
  [DocumentStatus.processing]: 'text-orange-700 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30',
  [DocumentStatus.success]: 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
  [DocumentStatus.failed]: 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
}

// FS-488: map enum values to translation keys. The DocumentStatus enum's
// string values are the canonical English labels — kept as enum values for
// backward compatibility with any caller comparing `status === 'Uploading'`
// — but the rendered text is now translated via the `data_catalog:document_status`
// namespace. Falls back to the raw enum value if the key is missing, so an
// unknown status string still renders something instead of an empty span.
const statusI18nKeys: Record<DocumentStatus, string> = {
  [DocumentStatus.pending]: 'data_catalog:document_status.pending',
  [DocumentStatus.uploading]: 'data_catalog:document_status.uploading',
  [DocumentStatus.processing]: 'data_catalog:document_status.processing',
  [DocumentStatus.success]: 'data_catalog:document_status.success',
  [DocumentStatus.failed]: 'data_catalog:document_status.failed',
}

const StatusTag = ({ status }: StatusTagProps) => {
  const { t } = useTranslation()
  const i18nKey = statusI18nKeys[status]
  const label = i18nKey ? t(i18nKey, { defaultValue: status }) : status

  return (
    <span
      className={cn(
        'w-fit inline rounded-lg py-1 px-3 text-center text-sm font-medium',
        statusStyles[status] || statusStyles[DocumentStatus.processing]
      )}
    >
      {label}
    </span>
  )
}

export default StatusTag
